import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { CabinetProvider, useCabinet } from "../context/CabinetContext";
import { getSkinsCatalog, skinByFolder } from "../data/skinsCatalog";
import CabinetLayout from "../components/cabinet/CabinetLayout";
import TopScreen from "../components/cabinet/screens/TopScreen";
import WallScreen from "../components/cabinet/screens/WallScreen";
import GamesScreen from "../components/cabinet/screens/GamesScreen";
import HomeScreen from "../components/cabinet/screens/HomeScreen";
import MyGamesScreen from "../components/cabinet/screens/MyGamesScreen";
import MeScreen from "../components/cabinet/screens/MeScreen";
import ShopScreen from "../components/cabinet/screens/ShopScreen";
import AvatarScreen from "../components/cabinet/screens/AvatarScreen";
import FriendsScreen from "../components/cabinet/screens/FriendsScreen";
import MoreScreen from "../components/cabinet/screens/MoreScreen";
import ProfileScreen from "../components/cabinet/screens/ProfileScreen";
import GameScreen from "../components/cabinet/screens/GameScreen";
import UiScreen from "../components/cabinet/screens/UiScreen";
import usePageTitle from "../hooks/usePageTitle";

/* пункты меню кабинета — 1-в-1 из MainScreen.kt */
const TABS = [
  { id: "feed", label: "Главная", icon: "home" },
  { id: "wall", label: "Стена", icon: "chat" },
  { id: "top", label: "Топ", icon: "trophy" },
  { id: "avatar", label: "Аватар", icon: "userCircle" },
  { id: "friends", label: "Друзья", icon: "users" },
  { id: "shop", label: "Магазин", icon: "palette" },
  { id: "more", label: "Ещё", icon: "more" },
];

/* Соответствие под-путей кабинета и разделов. Главные вкладки + подразделы
   «Ещё» (settings/help/about/wiki) — у каждого свой URL под /app. */
const PATH_TO_VIEW = {
  "": { tab: "feed", sub: null },
  "wall": { tab: "wall", sub: null },
  "games": { tab: "games", sub: null },
  "my": { tab: "my", sub: null },
  "me": { tab: "me", sub: null },
  "shop": { tab: "shop", sub: null },
  "top": { tab: "top", sub: null },
  "avatar": { tab: "avatar", sub: null },
  "friends": { tab: "friends", sub: null },
  "more": { tab: "more", sub: null },
  "settings": { tab: "more", sub: "settings" },
  "help": { tab: "more", sub: "help" },
  "about": { tab: "more", sub: "about" },
  "wiki": { tab: "more", sub: "wiki" },
  "account": { tab: "more", sub: "account" },
  "game": { tab: "feed", sub: null },
  /* Витрина дизайн-системы. В меню её нет намеренно — это инструмент
     разработки, а не раздел для игроков. Открывается прямой ссылкой. */
  "ui": { tab: "ui", sub: null },
};
/* Обратное: раздел/подраздел → под-путь (для navigate). */
const SUB_TO_PATH = {
  settings: "/app/settings",
  help: "/app/help",
  about: "/app/about",
  wiki: "/app/wiki",
  account: "/app/account",
};
const TAB_TO_PATH = {
  feed: "/app",
  wall: "/app/wall",
  games: "/app/games",
  my: "/app/my",
  me: "/app/me",
  shop: "/app/shop",
  top: "/app/top",
  avatar: "/app/avatar",
  friends: "/app/friends",
  more: "/app/more",
};

/**
 * CabinetPage — кабинет Рублокса (внутренняя часть после входа).
 * Навигация между 5 экранами через activeTab + детальная страница игры.
 * Гость сюда не попадает — редирект на лендинг (см. эффект ниже).
 */
function CabinetInner() {
  usePageTitle("Кабинет");
  const { isAuthenticated, initializing } = useAuth();
  const { selectedSkin } = useCabinet();
  const navigate = useNavigate();
  const location = useLocation();

  // Активный раздел определяется URL'ом (/app, /app/top, /app/settings, ...).
  // Это даёт прямые ссылки, работу кнопок «назад/вперёд» и refresh.
  const seg = location.pathname.replace(/^\/app\/?/, "").split("/")[0] || "";
  const view = PATH_TO_VIEW[seg] || PATH_TO_VIEW[""];
  const activeTab = view.tab;
  const moreSub = view.sub;

  // Страница игры теперь имеет свой URL: /app/game/:id — им можно делиться,
  // работает «назад/вперёд» и refresh. gameId берём из пути.
  const openGameId = (() => {
    if (seg !== "game") return null;
    const id = parseInt(location.pathname.split("/")[3] || "", 10);
    return !Number.isNaN(id) && id > 0 ? id : null;
  })();

  // Обратная совместимость: /app?game=<id> (возврат из плеера, см. exitPlayer
  // в rublox-player/.../KubikonPlayer.jsx) → редиректим на /app/game/<id>.
  const [searchParams] = useSearchParams();
  useEffect(() => {
    const g = searchParams.get("game");
    if (g) {
      const id = parseInt(g, 10);
      if (!Number.isNaN(id) && id > 0) {
        navigate(`/app/game/${id}`, { replace: true });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // avatar.png выбранного скина — для иконки вкладки «Аватар»
  const [skinAvatar, setSkinAvatar] = useState(null);

  /* открыть конкретный саб-экран MoreScreen из sidebar (десктоп) — через URL */
  const openMoreSub = (subId) => {
    navigate(SUB_TO_PATH[subId] || "/app/more");
  };

  /* гость → на лендинг */
  useEffect(() => {
    if (!initializing && !isAuthenticated) {
      navigate("/", { replace: true });
    }
  }, [initializing, isAuthenticated, navigate]);

  /* подтягиваем avatar.png выбранного скина */
  useEffect(() => {
    let cancelled = false;
    getSkinsCatalog()
      .then((catalog) => {
        if (cancelled) return;
        const skin = skinByFolder(catalog, selectedSkin);
        setSkinAvatar(skin ? skin.avatar : null);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [selectedSkin]);

  if (initializing || !isAuthenticated) {
    return (
      <div
        style={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: "#f5f7fb",
        }}
      >
        <div className="rb-cab-spinner" />
      </div>
    );
  }

  // детальная страница игры — свой URL /app/game/:id, поверх всего, без меню
  if (openGameId != null) {
    return (
      <CabinetLayout
        tabs={TABS}
        activeTab={activeTab}
        onTabChange={(id) => navigate(TAB_TO_PATH[id] || "/app")}
        skinAvatar={skinAvatar}
        fullscreen
      >
        <GameScreen
          gameId={openGameId}
          onBack={() => navigate("/app")}
        />
      </CabinetLayout>
    );
  }

  return (
    <CabinetLayout
      tabs={TABS}
      activeTab={activeTab}
      activeSub={moreSub}
      onTabChange={(id) => {
        /* Чип с именем и балансами в сайдбаре ведёт в ЛИЧНЫЙ профиль
           (/app/me), туда же, куда пункт меню «Профиль».
           Раньше он уводил на ПУБЛИЧНУЮ страницу (/app/profile/<id>) —
           получалось два разных места про одного и того же человека:
           клик по своему имени открывал «как меня видят другие», а пункт
           меню — редактирование. Понять, где ты находишься, было нельзя.
           Публичная страница осталась и открывается явной кнопкой
           «Как видят другие» внутри профиля. */
        navigate(TAB_TO_PATH[id] || "/app");
      }}
      onOpenMoreSub={openMoreSub}
      skinAvatar={skinAvatar}
    >
      {activeTab === "feed" && <HomeScreen />}
      {activeTab === "games" && <GamesScreen />}
      {activeTab === "my" && <MyGamesScreen />}
      {activeTab === "me" && <MeScreen />}
      {activeTab === "wall" && <WallScreen />}
      {activeTab === "shop" && <ShopScreen />}
      {activeTab === "top" && <TopScreen />}
      {activeTab === "avatar" && <AvatarScreen />}
      {activeTab === "friends" && <FriendsScreen />}
      {activeTab === "profile" && <ProfileScreen />}
      {activeTab === "ui" && <UiScreen />}
      {activeTab === "more" && (
        <MoreScreen
          key={moreSub || "root"}
          initialSub={moreSub}
          onNavigate={(id) => navigate(TAB_TO_PATH[id] || "/app")}
          onOpenSub={(subId) => navigate(SUB_TO_PATH[subId] || "/app/more")}
        />
      )}
    </CabinetLayout>
  );
}

export default function CabinetPage() {
  return (
    <CabinetProvider>
      <CabinetInner />
    </CabinetProvider>
  );
}
